import { ValidatorFn } from '@angular/forms';
import { InputControlType } from '../../enum';
import { BaseInput, BaseInputConstructor } from './base-input';

interface FileUploadContructorInterface extends BaseInputConstructor {
    accept?: string | undefined;
    cancelIcon?: string | null;
    cancelLabel?: string | null;
    cancelStyleClass?: string | null;
    chooseIcon?: string | null;
    chooseLabel?: string | null;
    chooseStyleClass?: string | null;
    defaultFileName?: string | null;
    fileLimit?: number | null;
    files?: File[] | null;
    maxFileSize?: number | null;
    mode?: 'basic' | 'advanced';
    multiple?: boolean;
    removeStyleClass?: string | null;
    showCancelButton?: boolean;
    showUploadButton?: boolean;
    styleClass?: string | null;
    uploadIcon?: string | null;
    uploadLabel?: string | null;
    uploadStyleClass?: string | null;
}

/**
 * FileUpload is an advanced uploader with dragdrop support, multi file uploads, auto uploading, progress tracking and validations.
 *
 * @export
 * @class FileUploadInput
 * @typedef {FileUploadInput}
 * @extends {BaseInput}
 * @see https://primeng.org/fileupload#api.fileupload
 */
export class FileUploadInput extends BaseInput {
    override inputType = 'file-upload';

    /**
     * Comma-separated list of pattern to restrict the allowed file types. Can be any combination of either the MIME types (such as "image/*") or the file extensions (such as ".jpg").
     *
     * @type {string|null}
     */
    accept: string | undefined = undefined;

    cancelIcon: string | null = null;
    cancelLabel: string | null = null;
    cancelStyleClass: string | null = null;

    chooseIcon: string | null = null;
    chooseLabel: string | null = null;
    chooseStyleClass: string | null = null;

    uploadIcon: string | null = null;
    uploadLabel: string | null = null;
    uploadStyleClass: string | null = null;

    showUploadButton = true;
    showCancelButton = true;

    mode: 'basic' | 'advanced' = 'advanced';
    fileLimit: number | null = null;
    removeStyleClass: string | null = null;

    /**
     * Maximum file size allowed in bytes.
     *
     * @type {(number | null)}
     */
    maxFileSize: number | null = null;

    /**
     * Used to select multiple files at once from file dialog.
     *
     * @type {boolean}
     */
    multiple = false;

    /**
     * Class of the element.
     *
     * @type {string|null}
     */
    styleClass: string | null = null;

    type: InputControlType = 'file-upload';

    files: File[] | null = null;
    defaultFileName: string | null = null;

    /**
     * Creates an instance of FileUploadInput.
     *
     * @constructor
     * @param options
     */
    constructor(options: FileUploadContructorInterface) {
        super(options);

        if ('undefined' !== typeof options.accept) {
            this.accept = options.accept;
        }
        if ('undefined' !== typeof options.multiple) {
            this.multiple = options.multiple;
        }
        if ('undefined' !== typeof options.maxFileSize) {
            this.maxFileSize = options.maxFileSize;
        }
        if ('undefined' !== typeof options.styleClass) {
            this.styleClass = options.styleClass;
        }
        if ('undefined' !== typeof options.chooseLabel) {
            this.chooseLabel = options.chooseLabel;
        }
        if ('undefined' !== typeof options.uploadLabel) {
            this.uploadLabel = options.uploadLabel;
        }
        if ('undefined' !== typeof options.cancelLabel) {
            this.cancelLabel = options.cancelLabel;
        }
        if ('undefined' !== typeof options.chooseIcon) {
            this.chooseIcon = options.chooseIcon;
        }
        if ('undefined' !== typeof options.uploadIcon) {
            this.uploadIcon = options.uploadIcon;
        }
        if ('undefined' !== typeof options.cancelIcon) {
            this.cancelIcon = options.cancelIcon;
        }
        if ('undefined' !== typeof options.showUploadButton) {
            this.showUploadButton = options.showUploadButton;
        }
        if ('undefined' !== typeof options.showCancelButton) {
            this.showCancelButton = options.showCancelButton;
        }
        if ('undefined' !== typeof options.mode) {
            this.mode = options.mode;
        }
        if ('undefined' !== typeof options.fileLimit) {
            this.fileLimit = options.fileLimit;
        }
        if ('undefined' !== typeof options.cancelStyleClass) {
            this.cancelStyleClass = options.cancelStyleClass;
        }
        if ('undefined' !== typeof options.chooseStyleClass) {
            this.chooseStyleClass = options.chooseStyleClass;
        }
        if ('undefined' !== typeof options.uploadStyleClass) {
            this.uploadStyleClass = options.uploadStyleClass;
        }
        if ('undefined' !== typeof options.removeStyleClass) {
            this.removeStyleClass = options.removeStyleClass;
        }
        if ('undefined' !== typeof options.files) {
            this.files = options.files;
        }
        if ('undefined' !== typeof options.defaultFileName) {
            this.defaultFileName = options.defaultFileName;
        }

        const validators: ValidatorFn[] = [];
        if (this.validator) {
            validators.push(this.validator);
        }

        this.setValidators(validators);
    }

    override clone(): FileUploadInput {
        return new FileUploadInput(this.__internalConstructor__);
    }
}
